import React, { useEffect, useState } from 'react';
// import { ID } from '../lib/appwrite';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './SalesHome.css';
import Modal from '../components/Modal';
import BillModal from '../components/BillModal';
import { api } from '../services/db';
import LoadingOverlay from '../components/LoadingOverlay';

export default function SalesHome() {
  const { logout, warehouseName, warehouseId, user, teamRoles } = useAuth();
  const navigate = useNavigate();
  const [type, setType] = useState('بيع');
  const [date] = useState(() => {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  });

  // DB Items from Appwrite
  const [dbItems, setDbItems] = useState([]);

  useEffect(() => {
    if (warehouseId) {
      loadDbItems();
    }
    // eslint-disable-next-line
  }, [warehouseId]);

  async function loadDbItems() {
    try {
      const data = await api.getItems(warehouseId);
      // Sort items by name alphabetically
      const sortedData = data.sort((a, b) => a.name.localeCompare(b.name, 'ar'));
      setDbItems(sortedData);
    } catch (e) {
      console.error("Failed to load items", e);
    }
  }

  const [items, setItems] = useState([{ name: '', qty: '', price: '', unitPrice: '' }]); // Added unitPrice
  const [incomingCert, setIncomingCert] = useState('');
  const [receiver, setReceiver] = useState('');
  const [loading, setLoading] = useState(false);

  // Helper for formatting money
  const formatMoney = (amount) => {
    return Math.round(Number(amount) || 0).toLocaleString('en-US');
  };

  // Modal State
  const [modal, setModal] = useState({ isOpen: false, message: '', type: 'success' });
  const [billModal, setBillModal] = useState({ isOpen: false, data: null });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    setLoading(false);
  }, []);

  useEffect(() => {
    if (type === 'بيع') {
      // Recalculate prices when switching to sale mode
      setItems(prev => prev.map(it => {
        if (it.name && it.qty) {
          const dbItem = dbItems.find(d => d.name === it.name);
          const uPrice = dbItem ? (parseFloat(dbItem.sell_price) || 0) : 0;
          return {
            ...it,
            unitPrice: uPrice,
            price: (parseFloat(it.qty) * uPrice).toFixed(0)
          };
        }
        return it;
      }));
    }
    // Reset receiver when not selling
    if (type !== 'بيع') {
      setReceiver('');
    }
  }, [type, dbItems]);

  function addItemRow() {
    setItems(prev => [...prev, { name: '', qty: '', price: '', unitPrice: '' }]);
  }

  function removeItemRow(idx) {
    setItems(prev => prev.filter((_, i) => i !== idx));
  }

  function updateItem(idx, field, value) {
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;

      let newItem = { ...it, [field]: value };

      // Logic for Auto-Calculation
      if (type === 'بيع') {
        if (field === 'name') {
          // Fetch unit price from DB
          const dbItem = dbItems.find(d => d.name === value);
          const uPrice = dbItem ? (parseFloat(dbItem.sell_price) || 0) : 0;
          newItem.unitPrice = uPrice;
          // Recalculate if qty exists
          if (newItem.qty) {
            newItem.price = (parseFloat(newItem.qty) * uPrice).toFixed(0);
          }
        } else if (field === 'qty') {
          // Recalculate price
          const uPrice = parseFloat(newItem.unitPrice) || 0;
          if (value) {
            newItem.price = (parseFloat(value) * uPrice).toFixed(0);
          } else {
            newItem.price = '';
          }
        }
      }

      return newItem;
    }));
  }

  // Calculate total price
  const totalPrice = items.reduce((sum, item) => {
    if (type === 'بيع' && item.price) {
      return sum + (parseFloat(item.price) || 0);
    }
    return sum;
  }, 0);

  async function addTransaction() {
    // Validations
    if (!date) {
      setModal({ isOpen: true, message: 'يرجى إدخال التاريخ.', type: 'error' });
      return;
    }

    if (items.some(it => !it.name || it.name === '')) {
      setModal({ isOpen: true, message: 'يرجى تحديد الصنف لجميع الصفوف.', type: 'error' });
      return;
    }

    if (items.some(it => !it.qty || Number(it.qty) <= 0)) {
      setModal({ isOpen: true, message: 'يرجى تحديد الكمية لجميع الصفوف.', type: 'error' });
      return;
    }

    if (type === 'بيع') {
      if (!receiver) {
        setModal({ isOpen: true, message: 'يرجى إدخال اسم المستلم.', type: 'error' });
        return;
      }
      if (items.some(it => !it.price || Number(it.price) <= 0)) {
        setModal({ isOpen: true, message: 'يرجى تحديد السعر لجميع الصفوف.', type: 'error' });
        return;
      }
    }

    if (type === 'وارد') {
      if (!incomingCert) {
        setModal({ isOpen: true, message: 'يرجى إدخال شهادة الوارد أو اسم الجهة.', type: 'error' });
        return;
      }
    }

    setIsProcessing(true);
    await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate processing

    try {
      // Execute Transaction via Appwrite Transactions API
      const result = await api.createSaleTransaction({
        items: items.map(it => {
          // Find reference DB item to ensure we have the ID
          // dbItems contains the full doc. items contains UI state.
          // We need to match names.
          const refItem = dbItems.find(d => d.name === it.name);
          return {
            ...it,
            id: refItem?.$id, // Pass ID for DB operations
            price: it.price,
            unitPrice: it.unitPrice,
            unitCost: refItem?.cost || 0 // Pass cost for profit calculation
          };
        }),
        type,
        date,
        receiver,
        incoming_cert: incomingCert,
        user_name: user?.name
      }, warehouseId);

      const receiptCode = result.code;

      if (type === 'بيع') {
        const billData = {
          date,
          receiptCode,
          receiver,
          items: items.map(item => ({
            ...item,
          })),
          total: formatMoney(totalPrice)
        };
        setBillModal({ isOpen: true, data: billData });
        resetForm();
      } else {
        setModal({
          isOpen: true,
          message: `تم حفظ المعاملة بنجاح، رقم الإيصال: ${receiptCode}`,
          type: 'success'
        });
        resetForm();
      }
    } catch (error) {
      console.error("Transaction Error:", error);
      let errorMsg = `فشلت العملية: ${error.message}`;
      if (error.message && (error.message.includes("Failed to fetch") || error.message.includes("Network request failed"))) {
        errorMsg = "خطاء في الشبكة ، الرجاء المحاولة مرة اخرى";
      } else if (error.message && error.message.includes("exceeds minimum limit")) {
        errorMsg = "لا يوجد مخزون كافي لإتمام هذه العملية. يرجى التحقق من الكميات المتوفرة.";
      }
      setModal({ isOpen: true, message: errorMsg, type: 'error' });
    } finally {
      setIsProcessing(false);
    }
  }

  // const [requestId, setRequestId] = useState(ID.unique());

  function resetForm() {
    setItems([{ name: '', qty: '', price: '', unitPrice: '' }]);
    setIncomingCert('');
    setReceiver('');
    // setRequestId(ID.unique());
  }

  const [pendingAction, setPendingAction] = useState(null);

  function handleCancel() {
    setPendingAction('cancelTx');
    setModal({ isOpen: true, message: 'هل أنت متأكد من إلغاء المعاملة؟', type: 'confirm' });
  }

  function handleLogoutClick() {
    setPendingAction('logout');
    setModal({ isOpen: true, message: 'هل انت متأكد من تسجيل الخروج؟', type: 'confirm' });
  }

  async function onConfirmAction() {
    if (pendingAction === 'cancelTx') {
      resetForm();
    } else if (pendingAction === 'logout') {
      await logout();
      navigate('/login');
    }
    closeModal();
  }

  function closeModal() {
    setModal({ ...modal, isOpen: false });
    setPendingAction(null);
  }


  return (
    <div className="home-root" dir="rtl">
      <LoadingOverlay isLoading={isProcessing || loading} />
      <Modal
        isOpen={modal.isOpen}
        message={modal.message}
        type={modal.type}
        onClose={closeModal}
        onConfirm={pendingAction ? onConfirmAction : null}
      />
      <BillModal
        isOpen={billModal.isOpen}
        data={billModal.data}
        onClose={() => setBillModal({ ...billModal, isOpen: false })}
      />
      <div className="container">
        <button id="logout" onClick={handleLogoutClick}>تسجيل الخروج</button>
        <h1 className="brand"> نظام مبيعات {warehouseName || 'نظام المبيعات'}</h1>

        {/* Mobile Hamburger Button */}
        <button className="mobile-menu-btn" onClick={() => setIsSidebarOpen(true)}>
          ☰
        </button>

        {/* Sidebar */}
        <div className={`sidebar ${isSidebarOpen ? 'open' : ''} `}>
          <button className="close-sidebar-btn" onClick={() => setIsSidebarOpen(false)}>✕</button>

          <div className="sidebar-content">
            <Link to="/sales-previous" onClick={() => setIsSidebarOpen(false)}><button className="sidebar-btn">معاملات سابقة</button></Link>
            <Link to="/sales-daily" onClick={() => setIsSidebarOpen(false)}><button className="sidebar-btn">التقرير اليومي</button></Link>
            {/* <button className="sidebar-btn" onClick={() => { setIsSidebarOpen(false); dummyAction('التقرير الأسبوعي') }}>التقرير الأسبوعي</button> */}
            <Link to="/sales-monthly" onClick={() => setIsSidebarOpen(false)}><button className="sidebar-btn">التقرير الشهري</button></Link>
            <Link to="/sales-items" onClick={() => setIsSidebarOpen(false)}><button className="sidebar-btn">إدارة الأصناف</button></Link>

            {/* Show "Store System" only if NOT salesOnly */}
            {teamRoles && !teamRoles.includes('salesOnly') && (
              <Link to="/home" onClick={() => setIsSidebarOpen(false)}><button className="sidebar-btn">نظام المخازن</button></Link>
            )}
          </div>

          <div className="sidebar-footer">
            <button id="logout-sidebar" onClick={handleLogoutClick}>تسجيل الخروج</button>
          </div>
        </div>

        {/* Overlay for closing sidebar when clicking outside */}
        {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)}></div>}

        <div className="actions desktop-only">
          <Link to="/sales-previous"><button id="previous-transactions-btn">معاملات سابقة</button></Link>
          <Link to="/sales-daily"><button id="daily-report-btn">التقرير اليومي</button></Link>
          {/* <button id="weekly-report-btn" onClick={() => dummyAction('التقرير الأسبوعي')}>التقرير الأسبوعي</button> */}
          <Link to="/sales-monthly"><button id="monthly-report-btn">التقرير الشهري</button></Link>
          <Link to="/sales-items"><button id="items-btn">إدارة الأصناف</button></Link>

          {/* Show "Store System" only if NOT salesOnly */}
          {teamRoles && !teamRoles.includes('salesOnly') && (
            <Link to="/home"><button id="store-system-btn">نظام المخازن</button></Link>
          )}
        </div>

        <div className="input-form">
          <h2>إدخال معاملة مبيعات</h2>

          <div className="form-group">
            <label htmlFor="type">نوع الحركة:</label>
            <select id="type" value={type} onChange={e => setType(e.target.value)}>
              <option value="بيع">بيع</option>
              <option value="وارد">وارد</option>
            </select>
          </div>

          {type === 'بيع' && (
            <div className="form-group">
              <label>اسم المستلم:</label>
              <input type="text" value={receiver} onChange={e => setReceiver(e.target.value)} placeholder="اسم العميل / المستلم" />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="date">التاريخ:</label>
            <input id="date" type="date" value={date} readOnly />
          </div>

          <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label className='the-items-label'>الأصناف :</label>
            <div id="items-list">
              {items.map((it, idx) => (
                <div key={idx} className="item-row">
                  <select value={it.name} onChange={e => updateItem(idx, 'name', e.target.value)} style={{ flex: 2 }}>
                    <option value="" disabled>حدد الصنف</option>
                    {dbItems
                      .filter(opt => !items.map(i => i.name).includes(opt.name) || opt.name === it.name)
                      .map(opt => <option key={opt.$id} value={opt.name}>{opt.name}</option>)}
                  </select>
                  <input
                    type="number"
                    min="1"
                    placeholder="الكمية"
                    value={it.qty}
                    onChange={e => updateItem(idx, 'qty', e.target.value)}
                    style={{ flex: 1 }}
                  />

                  {/* Price fields - only show when type is "بيع" */}
                  {type === 'بيع' && (
                    <>
                      <input
                        type="text"
                        readOnly // Read only now
                        placeholder="السعر الإجمالي"
                        value={it.price ? formatMoney(it.price) : ''}
                        className="price-input"
                        style={{ flex: 1, fontWeight: 'bold', backgroundColor: '#e9ecef' }}
                      />
                    </>
                  )}

                  <button type="button" className="remove-btn" onClick={() => removeItemRow(idx)}>-</button>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 8, textAlign: 'left' }}>
              <button type="button" id="add-item-btn" onClick={addItemRow}>إضافة صنف</button>
            </div>
          </div>

          {type === 'وارد' && (
            <div id="incoming-fields">
              <div className="form-group">
                <label>شهادة الوارد او اسم الجهة:</label>
                <input type="text" value={incomingCert} onChange={e => setIncomingCert(e.target.value)} placeholder="اسم الجهة أو شهادة الوارد" />
              </div>
              <div className="form-group">
                <label>رقم الإيصال:</label>
                <input type="text" readOnly value="سيتم التوليد تلقائياً" style={{ backgroundColor: '#f0f0f0', color: '#888' }} />
              </div>
            </div>
          )}

          {type === 'بيع' && (
            <div className="total-section">
              <div className="form-group">
                <label>الإجمالي:</label>
                <input
                  type="text"
                  readOnly
                  value={formatMoney(totalPrice)}
                  className="total-display"
                // style={{ backgroundColor: '#fff3cd', fontWeight: 'bold', fontSize: '1.2rem' }}
                />
              </div>
            </div>
          )}

          <div className="form-group" style={{ marginTop: 20 }}>
            <button onClick={addTransaction} style={{ flex: 1 }}>حفظ معاملة ({type})</button>
            <button className='cancel-btn' onClick={handleCancel}>إلغاء</button>
          </div>
        </div>
      </div>
    </div>
  );
}
